import { useCallback, useRef } from 'react';
import styled, { css } from 'styled-components';
import { LuPanelLeftClose, LuPanelLeftOpen, LuClipboardList, LuSettings, LuTarget } from 'react-icons/lu';
import { TbVideo } from 'react-icons/tb';

import SidebarSection from './SidebarSection';
import ProjectSection from './ProjectSection';
import VideoSection from './VideoSection';
import JobSection from './JobSection';
import DetectionsSection from './DetectionsSection';

const Sidebar = styled.div`
	background: ${({ theme }) => theme.colors.modal};
	border-right: 1px solid ${({ theme }) => theme.colors.border};
	overflow-y: auto;
	overflow-x: hidden;
	display: flex;
	flex-direction: column;
	scrollbar-width: thin;
	scrollbar-color: ${({ theme }) => theme.colors.backgroundAccent} transparent;

	&::-webkit-scrollbar {
		width: 4px;
	}
	&::-webkit-scrollbar-thumb {
		background: ${({ theme }) => theme.colors.backgroundAccent};
		border-radius: 2px;
	}
`;

const SidebarHeader = styled.div`
	padding: 14px 14px 10px;
	border-bottom: 1px solid ${({ theme }) => theme.colors.border};
	display: flex;
	align-items: center;
	justify-content: space-between;
	position: sticky;
	top: 0;
	background: ${({ theme }) => theme.colors.modal};
	z-index: 5;
	min-height: 44px;

	${({ $collapsed }) =>
		$collapsed &&
		css`
			justify-content: center;
			padding: 10px 8px;
		`}
`;

const SidebarTitle = styled.span`
	font-size: 11px;
	text-transform: uppercase;
	letter-spacing: 1px;
	color: ${({ theme }) => theme.colors.textAccent};
	font-weight: 600;
	${({ $collapsed }) => $collapsed && 'display: none;'}
`;

const ToggleButton = styled.button`
	width: 28px;
	height: 28px;
	border-radius: 6px;
	background: ${({ theme }) => theme.colors.backgroundAccent};
	border: 1px solid ${({ theme }) => theme.colors.border};
	color: ${({ theme }) => theme.colors.textColor};
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	transition: all 0.2s;
	flex-shrink: 0;

	&:hover {
		background: ${({ theme }) => theme.colors.green};
		border-color: ${({ theme }) => theme.colors.green};
	}
`;

// CSS for collapsed icon-only mode applied via global wrapper
const CollapsedSectionStyles = css`
	/* Section headers in collapsed mode */
	[data-tooltip] {
		justify-content: center;
		padding: 10px 0;
		position: relative;
	}
	[data-tooltip] > div:first-child {
		gap: 0;
	}
	[data-tooltip] h3,
	[data-tooltip] + div {
		display: none !important;
	}
	[data-tooltip] > span:last-child {
		display: none;
	}
	[data-tooltip] span:first-child {
		font-size: 18px;
		width: auto;

		svg {
			width: 18px;
			height: 18px;
		}
	}
	[data-tooltip]:hover {
		background: rgba(32, 172, 107, 0.1);
	}
	/* Tooltip */
	[data-tooltip]::after {
		content: attr(data-tooltip);
		position: absolute;
		left: 48px;
		top: 50%;
		transform: translateY(-50%);
		background: #2a2a2a;
		color: ${({ theme }) => theme.colors.textColor};
		border: 1px solid ${({ theme }) => theme.colors.border};
		padding: 4px 10px;
		border-radius: 5px;
		font-size: 11px;
		white-space: nowrap;
		opacity: 0;
		pointer-events: none;
		transition: opacity 0.15s;
		z-index: 20;
	}
	[data-tooltip]:hover::after {
		opacity: 1;
	}
`;

const CollapsedWrapper = styled.div`
	${({ $collapsed }) => $collapsed && CollapsedSectionStyles}
`;

const SECTIONS = [
	{ key: 'project', icon: <LuClipboardList size={14} />, title: 'Project' },
	{ key: 'video', icon: <TbVideo size={14} />, title: 'Video' },
	{ key: 'job', icon: <LuSettings size={14} />, title: 'Analysis' },
	{ key: 'detections', icon: <LuTarget size={14} />, title: 'Detections' },
];

const ProjectSidebar = ({ isCollapsed, onToggle, project, video, job, analysis }) => {
	const sectionRefs = useRef({});
	const savedStates = useRef({});

	const setSectionRef = useCallback((key, ref) => {
		sectionRefs.current[key] = ref;
	}, []);

	const handleToggle = useCallback(() => {
		if (!isCollapsed) {
			// Collapsing: save open states, close all
			SECTIONS.forEach(({ key }) => {
				const ref = sectionRefs.current[key];
				if (ref) {
					savedStates.current[key] = ref.isOpen;
					ref.close();
				}
			});
		} else {
			// Expanding: restore saved states
			SECTIONS.forEach(({ key }) => {
				const ref = sectionRefs.current[key];
				if (ref && savedStates.current[key]) {
					ref.open();
				}
			});
		}
		onToggle();
	}, [isCollapsed, onToggle]);

	const handleIconClick = useCallback(
		key => {
			if (!isCollapsed) return;
			// Expand sidebar and open only the clicked section
			SECTIONS.forEach(({ key: k }) => {
				const ref = sectionRefs.current[k];
				if (ref) ref.setOpen(k === key);
			});
			savedStates.current = {};
			onToggle();
		},
		[isCollapsed, onToggle]
	);

	const sectionContent = {
		project: <ProjectSection project={project} />,
		video: <VideoSection video={video} />,
		job: <JobSection job={job} />,
		detections: <DetectionsSection analysis={analysis} />,
	};

	return (
		<Sidebar>
			<SidebarHeader $collapsed={isCollapsed}>
				<SidebarTitle $collapsed={isCollapsed}>Project Info</SidebarTitle>
				<ToggleButton
					onClick={handleToggle}
					title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
					aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
					{isCollapsed ? <LuPanelLeftOpen size={14} /> : <LuPanelLeftClose size={14} />}
				</ToggleButton>
			</SidebarHeader>

			<CollapsedWrapper $collapsed={isCollapsed}>
				{SECTIONS.map(({ key, icon, title }) => (
					<div key={key} onClick={isCollapsed ? () => handleIconClick(key) : undefined}>
						<SidebarSection
							ref={ref => setSectionRef(key, ref)}
							icon={icon}
							title={title}
							defaultOpen={true}>
							{sectionContent[key]}
						</SidebarSection>
					</div>
				))}
			</CollapsedWrapper>
		</Sidebar>
	);
};

export default ProjectSidebar;
